import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, ChartBarIcon, DocumentTextIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { Select } from 'components/select'
import { MultiSelect } from 'components/multi-select'
import { Input } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import loadingImage from '../../assets/images/boat-loading.gif'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_NATIONALITY_WISE_PASSENGER_REPORT, SEARCH_LOCATION, GET_ROUTES_FOR_REPORTS, GET_BOAT_ROUTE_TRIP_STATUS } from 'graphql/reports/queries'
import { GET_COUNTRY_LIST } from 'graphql/common/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import i18next from 'i18next'
import { UsersIcon } from '@heroicons/react/24/outline'

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100]

const INITIAL_FILTER_STATE = {
    travel_date: {
        start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
    nationality_codes: [],
    route_ids: [],
    trip_ids: [],
    trip_statuses: [],
}

export default function NationalityPassengerReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    // States
    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [filterApplied, setFilterApplied] = useState(false)
    const [countries, setCountries] = useState([])
    const [routes, setRoutes] = useState([])
    const [trips, setTrips] = useState([])
    const [tripStatuses, setTripStatuses] = useState([])
    const [totalPassengers, setTotalPassengers] = useState(0)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
            setTotalPassengers(0)
        }
    }, [])

    // GraphQL query
    const [getNationalityReport, { loading: reportLoading }] = useLazyQuery(GET_NATIONALITY_WISE_PASSENGER_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.nationalityWisePassengerReport.success && data.nationalityWisePassengerReport.code === '200') {
                setData(data.nationalityWisePassengerReport.nationality_wise_passenger_report)
                setTotalRows(data.nationalityWisePassengerReport.total_count)
                
                // Calculate total passengers
                const total = data.nationalityWisePassengerReport.nationality_wise_passenger_report.reduce(
                    (sum, item) => sum + (item.total_passengers || 0),
                    0
                )
                setTotalPassengers(total)
            } else {
                handleError(data.nationalityWisePassengerReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const [getCountries] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
        onError: error => handleError(error.message),
    })

    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getBoatRoute.success && data.getBoatRoute.code === '200') {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
        onError: error => handleError(error.message),
    })

    const [getTripStatuses] = useLazyQuery(GET_BOAT_ROUTE_TRIP_STATUS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.boatRouteTripStatus) {
                setTripStatuses(data.boatRouteTripStatus)
            }
        },
        onError: error => handleError(error.message),
    })

    // Load dropdown data on mount
    useEffect(() => {
        getCountries({ variables: { code: '' } })
        getRoutes({
            variables: {
                page: { offset: 0, limit: 100 },
                routeId: '',
                routeName: '',
                statusCode: '',
            },
        })
        getTripStatuses({ variables: { code: '' } })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    // Update trips when routes change
    useEffect(() => {
        if (filterObj.route_ids?.length > 0) {
            const selectedRoutes = routes.filter(r => filterObj.route_ids.includes(r.route_id))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            // Remove duplicates based on trip_id
            const uniqueTrips = allTrips.filter((trip, index, self) =>
                index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
        }
    }, [filterObj.route_ids, routes])

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch nationality report'))
        setShowErrDialog(true)
    }

    const fetchReport = (page = 1) => {
        const offset = (page - 1) * perPage
        getNationalityReport({
            variables: {
                page: {
                    limit: perPage,
                    offset,
                },
                filter: {
                    travel_date: {
                        start_date: filterObj.travel_date.start_date,
                        end_date: filterObj.travel_date.end_date,
                    },
                    nationality_codes: filterObj.nationality_codes?.length > 0 ? filterObj.nationality_codes : null,
                    route_ids: filterObj.route_ids?.length > 0 ? filterObj.route_ids : null,
                    trip_ids: filterObj.trip_ids?.length > 0 ? filterObj.trip_ids : null,
                    trip_statuses: filterObj.trip_statuses?.length > 0 ? filterObj.trip_statuses : null,
                },
            },
        })
    }
    

    useEffect(() => {
        if (filterApplied) {
            fetchReport(currentPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    const handleApplyFilter = () => {
        setCurrentPage(1)
        setFilterApplied(true)
        fetchReport(1)
    }

    const handleClearFilter = () => {
        setFilterObj({
            ...INITIAL_FILTER_STATE,
            nationality_codes: [],
            route_ids: [],
            trip_ids: [],
            trip_statuses: [],
        })
        setCurrentPage(1)
        setPerPage(10)
        setFilterApplied(false)
        setData([])
        setTotalRows(0)
        setTotalPassengers(0)
        setTrips([])
    }

    const handleExportCSV = async () => {
        setExportLoading(true)
        try {
            const allData = []
            let offset = 0
            const batchSize = 100

            while (true) {
                const result = await getNationalityReport({
                    variables: {
                        page: {
                            limit: batchSize,
                            offset,
                        },
                        filter: {
                            travel_date: {
                                start_date: filterObj.travel_date.start_date,
                                end_date: filterObj.travel_date.end_date,
                            },
                            nationality_codes: filterObj.nationality_codes?.length > 0 ? filterObj.nationality_codes : null,
                            route_ids: filterObj.route_ids?.length > 0 ? filterObj.route_ids : null,
                            trip_ids: filterObj.trip_ids?.length > 0 ? filterObj.trip_ids : null,
                            trip_statuses: filterObj.trip_statuses?.length > 0 ? filterObj.trip_statuses : null,
                        },
                    },
                })
                

                if (
                    result.data?.nationalityWisePassengerReport?.success &&
                    result.data.nationalityWisePassengerReport.code === '200'
                ) {
                    const batch = result.data.nationalityWisePassengerReport.nationality_wise_passenger_report
                    allData.push(...batch)

                    if (batch.length < batchSize) {
                        break
                    }
                    offset += batchSize
                } else {
                    throw new Error(result.data?.nationalityWisePassengerReport?.message || 'Failed to fetch data')
                }
            }

            // Generate CSV
            const headers = ['Nationality', 'Travel Date', 'From', 'To', 'Passenger Count', 'Status']
            let csvContent = headers.join(',') + '\n'

            allData.forEach(row => {
                const nationality = i18next.language === 'ar' ? row.nationality?.country_ar : row.nationality?.country
                const from = i18next.language === 'ar' ? row.start_station?.ar : row.start_station?.en
                const to = i18next.language === 'ar' ? row.end_station?.ar : row.end_station?.en
                const status = i18next.language === 'ar' ? row.status?.description?.ar : row.status?.description?.en

                const values = [
                    `"${nationality || '-'}"`,
                    `"${row.travel_date || '-'}"`,
                    `"${from || '-'}"`,
                    `"${to || '-'}"`,
                    `"${row.total_passengers || 0}"`,
                    `"${status || '-'}"`,
                ]
                csvContent += values.join(',') + '\n'
            })

            // Add summary row
            csvContent += '\n'
            csvContent += `"Total Passengers","","","","${totalPassengers}",""\n`

            // Download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.setAttribute('href', url)
            link.setAttribute(
                'download',
                `nationality-passenger-report-${moment().format('YYYY-MM-DD')}.csv`
            )
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                name: t('Nationality'),
                selector: row => row.nationality?.country,
                sortable: true,
                cell: row => (
                    <div className="font-medium text-gray-900">
                        {i18next.language === 'ar' ? row.nationality?.country_ar : row.nationality?.country}
                    </div>
                ),
                width: '180px',
            },
            {
                name: t('Travel Date'),
                selector: row => row.travel_date,
                sortable: true,
                cell: row => <div>{row.travel_date ? moment(row.travel_date).format('DD MMM YYYY') : '-'}</div>,
                width: '150px',
            },
            {
                name: t('From'),
                selector: row => row.start_station?.en,
                sortable: true,
                cell: row => (
                    <div>{i18next.language === 'ar' ? row.start_station?.ar : row.start_station?.en}</div>
                ),
                width: '180px',
            },
            {
                name: t('To'),
                selector: row => row.end_station?.en,
                sortable: true,
                cell: row => (
                    <div>{i18next.language === 'ar' ? row.end_station?.ar : row.end_station?.en}</div>
                ),
                width: '180px',
            },
            {
                name: t('Passenger Count'),
                selector: row => row.total_passengers,
                sortable: true,
                cell: row => <div className="font-semibold text-gray-900">{row.total_passengers || 0}</div>,
                width: '150px',
            },
            {
                name: t('Status'),
                selector: row => row.status?.description?.en,
                sortable: true,
                cell: row => {
                    const status = i18next.language === 'ar' ? row.status?.description?.ar : row.status?.description?.en
                    const statusCode = row.status?.code

                    let badgeColor = 'zinc'
                    if (statusCode === 'CMP' || statusCode === 'CNF'|| statusCode === 'ACT') badgeColor = 'emerald'
                    else if (statusCode === 'CXL' || statusCode === 'TCL') badgeColor = 'rose'
                    else if (statusCode === 'NOS') badgeColor = 'amber'

                    return <Badge color={badgeColor}>{status || '-'}</Badge>
                },
                width: '150px',
            },
        ],
        [t, i18next.language]
    )

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18next.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18next.language === 'ar' ? 'right' : 'left',
            },
        },
        pagination: {
            style: {
                direction: i18next.language === 'ar' ? 'rtl' : 'ltr',
            },
            pageButtonsStyle: {
                direction: i18next.language === 'ar' ? 'rtl' : 'ltr',
                marginLeft: i18next.language === 'ar' ? '0' : '0.5rem',
                marginRight: i18next.language === 'ar' ? '0.5rem' : '0',
            },
        },
        noData: {
            style: {
                direction: i18next.language === 'ar' ? 'rtl' : 'ltr',
            },
        },
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <UsersIcon className="w-[20px] h-[20px] text-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Nationality Summary')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Passenger Count by Nationality')}
                            </p>
                        </div>
                    </h1>

                    {/* Filters */}
                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                {t('Travel Date Range')}
                            </label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(1, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.travel_date.start_date, filterObj.travel_date.end_date]}
                                onChange={dates => {
                                    if (dates.length === 2) {
                                        setFilterObj({
                                            ...filterObj,
                                            travel_date: {
                                                start_date: moment(dates[0]).format('YYYY-MM-DD'),
                                                end_date: moment(dates[1]).format('YYYY-MM-DD'),
                                            },
                                        })
                                    }
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">
                                {t('Nationality')}
                            </label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.nationality_codes || []}
                                onChange={(values) => setFilterObj({ ...filterObj, nationality_codes: values })}
                                options={countries.map(country => ({
                                    value: country.alpha_iso2,
                                    label: i18next.language === 'ar' ? country.country_ar : country.country,
                                }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">
                                {t('Route')}
                            </label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.route_ids || []}
                                onChange={(values) =>
                                    setFilterObj({ ...filterObj, route_ids: values, trip_ids: [] })
                                }
                                options={routes.map(route => ({
                                    value: route.route_id,
                                    label: i18next.language === 'ar' ? route.route_name?.ar : route.route_name?.en,
                                }))}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">
                                {t('Trip')}
                            </label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.trip_ids || []}
                                onChange={(values) => setFilterObj({ ...filterObj, trip_ids: values })}
                                options={trips.map(trip => ({
                                    value: trip.trip_id,
                                    label: i18next.language === 'ar' ? trip.trip_name?.ar : trip.trip_name?.en,
                                }))}
                                disabled={filterObj.route_ids?.length === 0}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">
                                {t('Trip Status')}
                            </label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.trip_statuses || []}
                                onChange={(values) => setFilterObj({ ...filterObj, trip_statuses: values })}
                                options={tripStatuses.map(status => ({
                                    value: status.code,
                                    label: i18next.language === 'ar' ? status.description?.ar : status.description?.en,
                                }))}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handleApplyFilter}
                        className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                        disabled={reportLoading}
                    >
                        {t('Filter')}
                    </button>
                    <button
                        type="button"
                        onClick={handleClearFilter}
                        className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        disabled={reportLoading}
                    >
                        {t('Clear')}
                    </button>
                </div>
                {filterApplied && data.length > 0 && (
                    <button
                        type="button"
                        onClick={handleExportCSV}
                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={exportLoading}
                    >
                        {exportLoading ? (
                            <>
                                <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                {t('Exporting...')}
                            </>
                        ) : (
                            <>
                                <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                {t('Export to CSV')}
                            </>
                        )}
                    </button>
                )}
            </div>


            {reportLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                filterApplied && (
                    <>
                        {/* Summary Cards */}
                        {data.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                {/* Total Records Card */}
                                <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between hover:shadow-xl transition-shadow duration-200">
                                    <div>
                                        <h3 className="text-gray-600 text-sm font-medium mb-1">{t('Total Records')}</h3>
                                        <p className="text-3xl font-bold text-primary">{totalRows}</p>
                                    </div>
                                    <div className="bg-primary/10 p-4 rounded-xl flex items-center justify-center">
                                        <DocumentTextIcon className="w-7 h-7 text-primary/90" />
                                    </div>
                                </div>

                                {/* Total Passengers Card */}
                                <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between hover:shadow-xl transition-shadow duration-200">
                                    <div>
                                        <h3 className="text-gray-600 text-sm font-medium mb-1">{t('Total Passengers')}</h3>
                                        <p className="text-3xl font-bold text-emerald-600">{totalPassengers}</p>
                                    </div>
                                    <div className="bg-emerald-50 p-4 rounded-xl flex items-center justify-center">
                                        <UsersIcon className="w-7 h-7 text-emerald-600/90" />
                                    </div>
                                </div>

                                {/* Average Passengers per Record */}
                                {/* <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between hover:shadow-xl transition-shadow duration-200">
                                    <div>
                                        <h3 className="text-gray-600 text-sm font-medium mb-1">{t('Avg. Passengers/Record')}</h3>
                                        <p className="text-3xl font-bold text-blue-600">
                                            {totalRows > 0 ? (totalPassengers / totalRows).toFixed(1) : '0'}
                                        </p>
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-xl flex items-center justify-center">
                                        <ChartBarIcon className="w-7 h-7 text-blue-600/90" />
                                    </div>
                                </div> */}
                            </div>
                        )}
                        <DataTable
                            columns={columns}
                            data={data}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            paginationDefaultPage={currentPage}
                            paginationPerPage={perPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            customStyles={customStyles}
                            noDataComponent={
                                <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                                    <svg
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        className="mx-auto h-12 w-12 text-gray-400"
                                    >
                                        <path
                                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                            strokeWidth={2}
                                            vectorEffect="non-scaling-stroke"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {t('Please add or modify your search criteria and try again.')}
                                    </p>
                                    <div className="mt-6"></div>
                                </div>
                            }
                        />
                    </>
                )
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
